import { useState, useEffect } from 'react';
import './ContentEnginePage.css';
import { aiService } from '@/services/AIService';
import { usageService } from '@/services/UsageService';
import { youtubeService } from '@/services/YouTubeService';
import { cmsService } from '@/services/CMSService';
import { presetService, type Preset, type BlogOptions } from '@/services/PresetService';
import { designTemplateService, type DesignTemplate } from '@/services/DesignTemplateService';
import { ContentEditor } from '@/components/content/ContentEditor';
// import { PersonaBuilder } from '@/components/persona/PersonaBuilder'; // 인격체 설정 제거됨
import { FactCheckResult } from '@/components/factCheck/FactCheckResult';
import { factCheckService } from '@/services/FactCheckService';
import { PublishingTargets } from '@/components/publish/PublishingTargets';
import { TokenModal } from '@/components/publish/TokenModal';
import { PublishStatusModal } from '@/components/publish/PublishStatusModal';
import { PublishHistory } from '@/components/publish/PublishHistory';
import type { PublishPlatform } from '@/types/publish';
import type { BlogGenerationRequest, YouTubeScriptRequest, BlogGenerationResponse } from '@/types/ai';
import type { RateLimitInfo } from '@/types/usage';
import type { PersonalityProfile } from '@/types/personality';
import type { PersonaProfile } from '@/types/persona';
import type { FactCheckResult as FactCheckResultType } from '@/types/factCheck';
import type { YouTubeOptimization } from '@/services/YouTubeService';
import { PERSONALITY_PRESETS, AGE_GROUP_LABELS, GENDER_LABELS, OCCUPATION_LABELS, TONE_LABELS, PURPOSE_LABELS } from '@/types/personality';

export default function ContentEnginePage() {
  const [keyword, setKeyword] = useState('');
  const [focusKeyword, setFocusKeyword] = useState('');
  const [title, setTitle] = useState('');
  const [contentDirection, setContentDirection] = useState('');
  const [keywordCount, setKeywordCount] = useState(5);
  const [focusKeywordCount, setFocusKeywordCount] = useState(5);
  const [contentType, setContentType] = useState<'blog' | 'youtube' | null>(null);
  const [loading, setLoading] = useState<'blog' | 'yt' | null>(null);
  const [blogHtml, setBlogHtml] = useState<string>('');
  const [blogMetadata, setBlogMetadata] = useState<BlogGenerationResponse['data'] | null>(null);
  const [ytScript, setYtScript] = useState<string>('');
  const [ytOptimization, setYtOptimization] = useState<YouTubeOptimization | null>(null);
  const [loadingOptimization, setLoadingOptimization] = useState(false);
  const [ytVideoType, setYtVideoType] = useState<'tutorial' | 'review' | 'explainer' | 'listicle' | 'comparison' | 'vlog'>('tutorial');
  const [ytTargetDuration, setYtTargetDuration] = useState<'short' | 'medium' | 'long'>('medium');
  const [ytTone, setYtTone] = useState<'educational' | 'entertainment' | 'professional'>('educational');
  const [ytTimeline, setYtTimeline] = useState<Array<{timestamp: string; section: string; content: string; duration: number}>>([]);
  const [ytActiveTab, setYtActiveTab] = useState<'script' | 'timeline' | 'seo' | 'editor'>('script');
  const [ytEditorMode, setYtEditorMode] = useState<'preview' | 'edit' | 'source'>('preview');
  const [showTeleprompter, setShowTeleprompter] = useState(false);
  const [showCMSPanel, setShowCMSPanel] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string>('');
  const [rateLimit, setRateLimit] = useState<RateLimitInfo | null>(null);
  const [totalCost, setTotalCost] = useState(0);
  const [selectedPersonality, setSelectedPersonality] = useState<PersonalityProfile | null>(PERSONALITY_PRESETS[0] || null);
  const [selectedPersona, setSelectedPersona] = useState<Partial<PersonaProfile>>({});
  const [personaProfileId, setPersonaProfileId] = useState<number | null>(null);
  const [blogOptions, setBlogOptions] = useState<BlogOptions>({
    contentType: 'informational',
    targetLength: 1500,
    tone: 'professional',
    targetAudience: 'beginner',
    language: 'ko',
    emojiUsage: 'minimal',
    emoticonsAllowed: false,
    visualExpression: 'text-only'
  });
  const [priority, setPriority] = useState<'quality' | 'speed' | 'balance'>('balance');
  const [progress, setProgress] = useState<{ stage: string; completed: boolean }[]>([]);
  const [savedPresets, setSavedPresets] = useState<Preset[]>([]);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [showSavePresetModal, setShowSavePresetModal] = useState(false);
  const [designTemplates, setDesignTemplates] = useState<DesignTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<DesignTemplate | null>(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateCategory, setTemplateCategory] = useState<string>('all');
  const [factCheckResult, setFactCheckResult] = useState<FactCheckResultType | null>(null);
  const [loadingFactCheck, setLoadingFactCheck] = useState(false);
  const [enableFactCheck, setEnableFactCheck] = useState(false);
  const [aiProvider, setAiProvider] = useState<'claude' | 'openai'>('claude');
  const [aiModel, setAiModel] = useState<string>('claude-sonnet-4');
  const [showPublishPanel, setShowPublishPanel] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenModalPlatform, setTokenModalPlatform] = useState<PublishPlatform | null>(null);
  const [showPublishStatusModal, setShowPublishStatusModal] = useState(false);
  const [publishJobIds, setPublishJobIds] = useState<Record<PublishPlatform, string>>({} as Record<PublishPlatform, string>);
  const [showPublishHistory, setShowPublishHistory] = useState(false);

  useEffect(() => {
    loadUsageStats();
    loadPresets();
    loadDesignTemplates();
    
    // localStorage에서 이전 설정 복원 (React 앱 내부)
    restoreYouTubeOptionsFromLocalStorage();
    
    // 캐시된 스크립트 요청
    window.parent.postMessage({ type: 'REQUEST_CACHED_SCRIPT' }, window.location.origin);
    
    // URL 파라미터에서 키워드, 포커스 키워드, 제목, 제작 방향 및 타입 가져오기
    const hash = window.location.hash;
    const urlParams = new URLSearchParams(hash.split('?')[1] || '');
    const keywordParam = urlParams.get('keyword');
    const focusKeywordParam = urlParams.get('focus_keyword');
    const titleParam = urlParams.get('title');
    const contentDirectionParam = urlParams.get('content_direction');
    const typeParam = urlParams.get('type');
    
    // URL 파라미터가 있으면 항상 설정 (초기 로드 시에만)
    if (keywordParam) {
      setKeyword(decodeURIComponent(keywordParam));
    }
    
    if (focusKeywordParam) {
      setFocusKeyword(decodeURIComponent(focusKeywordParam));
    }
    
    if (titleParam) {
      setTitle(decodeURIComponent(titleParam));
    }
    
    if (contentDirectionParam) {
      setContentDirection(decodeURIComponent(contentDirectionParam));
    }
    
    if (typeParam === 'youtube') {
      setContentType('youtube');
    } else if (!contentType) {
      setContentType('blog');
    }
    
    // 부모 창에서 옵션 업데이트 메시지 수신
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'UPDATE_OPTIONS') {
        const options = event.data.options;
        
        // 블로그 옵션 업데이트
        if (options.contentType) {
          setBlogOptions(prev => ({
            ...prev,
            contentType: options.contentType,
            tone: options.tone || prev.tone,
            targetAudience: options.targetAudience || prev.targetAudience
          }));
        }
        
        if (options.targetLength) {
          const lengthMap: Record<string, number> = {
            short: 1000,
            medium: 1500,
            long: 2000
          };
          setBlogOptions(prev => ({
            ...prev,
            targetLength: lengthMap[options.targetLength] || prev.targetLength
          }));
        }
        
        if (options.priority) {
          setPriority(options.priority);
        }
        
        if (options.enableFactCheck !== undefined) {
          setEnableFactCheck(options.enableFactCheck);
        }
      } else if (event.data && event.data.type === 'UPDATE_YOUTUBE_OPTIONS') {
        const options = event.data.options;
        
        // YouTube 옵션 업데이트
        if (options.videoType) {
          setYtVideoType(options.videoType as any);
        }
        
        if (options.targetDuration) {
          setYtTargetDuration(options.targetDuration as any);
        }
        
        if (options.priority) {
          setPriority(options.priority);
        }
        
        if (options.enableFactCheck !== undefined) {
          setEnableFactCheck(options.enableFactCheck);
        }
        
        if (options.aiProvider) {
          setAiProvider(options.aiProvider as 'claude' | 'openai');
        }
        
        if (options.aiModel) {
          setAiModel(options.aiModel);
        }
        
        // 글 목적 및 글 의도를 인격체 설정에 반영
        if (options.purpose || options.intent) {
          setSelectedPersona(prev => ({
            ...prev,
            purpose: options.purpose || prev.purpose,
            intent: options.intent || prev.intent
          }));
        }
        
        // localStorage에 자동 저장
        saveYouTubeOptionsToLocalStorage(options);
      } else if (event.data && event.data.type === 'SAVE_SETTINGS') {
        // 부모 페이지에서 저장 요청 시
        const currentSettings = {
          aiProvider,
          aiModel,
          ytVideoType,
          ytTargetDuration,
          priority,
          enableFactCheck
        };
        saveYouTubeOptionsToLocalStorage(currentSettings);
      } else if (event.data && event.data.type === 'RESTORE_CACHED_SCRIPT') {
        // 캐시된 스크립트 복원
        const scriptData = event.data.scriptData;
        if (scriptData) {
          if (scriptData.script) setYtScript(scriptData.script);
          if (scriptData.keyword) setKeyword(scriptData.keyword);
          if (scriptData.timeline) setYtTimeline(scriptData.timeline);
          console.log('📂 캐시된 스크립트가 복원되었습니다');
        }
      } else if (event.data && event.data.type === 'GET_CURRENT_SCRIPT') {
        // 부모 페이지에서 현재 스크립트 요청
        window.parent.postMessage({
          type: 'CURRENT_SCRIPT_DATA',
          scriptData: {
            script: ytScript,
            keyword: keyword,
            timeline: ytTimeline,
            metadata: { videoType: ytVideoType, targetDuration: ytTargetDuration },
            videoType: ytVideoType,
            targetDuration: ytTargetDuration,
            aiModel: aiModel
          }
        }, window.location.origin);
      } else if (event.data && event.data.type === 'UPDATE_PERSONA') {
        const persona = event.data.persona;
        
        // 인격체 설정 업데이트 (purpose와 intent는 YouTube 옵션에서 관리)
        if (persona) {
          const { purpose, intent, ...personaWithoutPurposeIntent } = persona;
          setSelectedPersona(personaWithoutPurposeIntent);
          setPersonaProfileId(null); // 직접 설정 시 프리셋 해제
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    if (selectedTemplate) {
      designTemplateService.applyTemplateCSS(selectedTemplate);
    }
    return () => {
      if (selectedTemplate) {
        designTemplateService.removeTemplateCSS(selectedTemplate.template_id);
      }
    };
  }, [selectedTemplate]);

  const loadPresets = async () => {
    try {
      const presets = await presetService.list();
      setSavedPresets(presets);
    } catch (e) {
      console.error('프리셋 로드 실패:', e);
    }
  };

  const loadDesignTemplates = async () => {
    try {
      const templates = await designTemplateService.getAllTemplates();
      setDesignTemplates(templates);
    } catch (e) {
      console.error('디자인 템플릿 로드 실패:', e);
      // API 실패 시에도 계속 진행 (빈 배열로 설정)
      setDesignTemplates([]);
    }
  };

  const loadUsageStats = async () => {
    try {
      const limit = await usageService.getRateLimit(1);
      setRateLimit(limit);
      const records = await usageService.list();
      const cost = records.reduce((sum, r) => sum + (r.costUsd || 0), 0);
      setTotalCost(cost);
    } catch (e) {
      console.error('사용량 통계 로드 실패:', e);
    }
  };

  const runBlog = async () => {
    if (!keyword.trim()) return;
    setLoading('blog');
    setError('');
    
    // 진행 상황 초기화
    setProgress([
      { stage: '키워드 분석', completed: false },
      { stage: '콘텐츠 브리프 생성', completed: false },
      { stage: 'AI 초안 생성', completed: false },
      { stage: 'SEO 후처리', completed: false }
    ]);
    
    try {
      // 1단계: 키워드 분석
      setProgress(prev => prev.map((p, i) => i === 0 ? { ...p, completed: true } : p));
      
      const req: BlogGenerationRequest = {
        userId: 1,
        keyword: keyword.trim(),
        title: title.trim() || undefined,
        subject: contentDirection.trim() || undefined,
        seoOptimization: true,
        personalityId: selectedPersonality?.id,
        personaProfileId: personaProfileId || undefined,
        persona: Object.keys(selectedPersona).length > 0 ? selectedPersona as PersonaProfile : undefined,
        contentType: blogOptions.contentType,
        targetLength: blogOptions.targetLength === 1500 ? 'medium' : 
                      blogOptions.targetLength < 1000 ? 'short' : 'long',
        priority: priority,
        targetAudience: blogOptions.targetAudience,
        enableFactCheck: enableFactCheck
      };
      
      // 2단계: 콘텐츠 브리프 생성
      setProgress(prev => prev.map((p, i) => i === 1 ? { ...p, completed: true } : p));
      
      // 3단계: AI 초안 생성
      setProgress(prev => prev.map((p, i) => i === 2 ? { ...p, completed: true } : p));
      
      const res = await aiService.generateBlogContent(req);
      
      // 4단계: SEO 후처리
      setProgress(prev => prev.map((p, i) => i === 3 ? { ...p, completed: true } : p));
      if (res.success && res.data) {
        setBlogHtml(res.data.content);
        setBlogMetadata(res.data);
        
        // 팩트 체크 결과가 있으면 설정
        if (res.data.factCheck) {
          setFactCheckResult(res.data.factCheck);
        }
        
        // 사용량 기록
        await usageService.record({
          userId: 1,
          actionType: 'blog_generation',
          costUsd: res.data.generationStats?.costUsd || 0,
          timestamp: new Date().toISOString(),
        });
        await loadUsageStats();
      } else {
        setError('블로그 콘텐츠 생성 실패');
      }
    } catch (e: any) {
      setError(e?.message || '오류가 발생했습니다.');
    } finally {
      setLoading(null);
    }
  };

  const runFactCheck = async () => {
    if (!blogHtml || !keyword.trim()) {
      setError('콘텐츠와 키워드가 필요합니다.');
      return;
    }

    setLoadingFactCheck(true);
    setError('');

    try {
      const result = await factCheckService.checkFacts({
        content: blogHtml,
        keyword: keyword.trim(),
        contentId: blogMetadata?.id
      });

      if (result.success && result.data) {
        setFactCheckResult(result.data);
      } else {
        setError(result.error?.message || '팩트 체크에 실패했습니다.');
      }
    } catch (e: any) {
      setError(e?.message || '팩트 체크 중 오류가 발생했습니다.');
    } finally {
      setLoadingFactCheck(false);
    }
  };

  const runYouTube = async () => {
    if (!keyword.trim()) return;
    setLoading('yt');
    setError('');
    try {
      // 사이드바 옵션에서 purpose와 intent 가져오기
      const personaPurpose = (selectedPersona as any)?.purpose;
      const personaIntent = (selectedPersona as any)?.intent;
      
      const req: YouTubeScriptRequest = {
        userId: 1,
        keyword: keyword.trim(),
        title: title.trim() || undefined,
        subject: contentDirection.trim() || undefined,
        targetDuration: ytTargetDuration || 'medium',
        videoType: ytVideoType || 'tutorial',
        personalityId: selectedPersonality?.id,
        // 사이드바 옵션 추가
        purpose: personaPurpose || undefined,
        intent: personaIntent || undefined,
        enableFactCheck: enableFactCheck || false,
        // AI 모델 설정
        aiProvider: aiProvider || 'claude',
        aiModel: aiModel || 'claude-sonnet-4',
        // 인격체 정보도 함께 전달 (persona 객체)
        persona: Object.keys(selectedPersona).length > 0 ? selectedPersona as any : undefined,
      };
      
      console.log('📋 YouTube 스크립트 생성 요청 상세:', {
        keyword: req.keyword,
        title: req.title,
        subject: req.subject,
        targetDuration: req.targetDuration,
        videoType: req.videoType,
        personalityId: req.personalityId,
        purpose: req.purpose,
        intent: req.intent,
        enableFactCheck: req.enableFactCheck,
        persona: req.persona
      });
      console.log('📤 YouTube 스크립트 생성 요청:', req);
      
      const res = await aiService.generateYouTubeScript(req);
      console.log('📥 YouTube 스크립트 생성 응답:', res);
      
      if (res.success && res.data) {
        setYtScript(res.data.script);
        
        // 타임라인 추출
        if (res.data.metadata && (res.data.metadata as any).timeline) {
          setYtTimeline((res.data.metadata as any).timeline);
        }
        
        // 스크립트를 캐시에 저장 (부모 페이지로 전송)
        window.parent.postMessage({
          type: 'SAVE_SCRIPT_TO_CACHE',
          script: res.data.script,
          keyword: keyword,
          timeline: (res.data.metadata as any)?.timeline || [],
          metadata: res.data.metadata
        }, window.location.origin);
        
        // 유튜브 최적화 자동 로드
        setLoadingOptimization(true);
        try {
          const optimization = await youtubeService.getOptimization(
            keyword.trim(),
            res.data.script,
            res.data.metadata || {}
          );
          setYtOptimization(optimization);
        } catch (e) {
          console.error('최적화 로드 실패:', e);
        } finally {
          setLoadingOptimization(false);
        }
        
        // 사용량 기록
        await usageService.record({
          userId: 1,
          actionType: 'youtube_generation',
          costUsd: res.data.generationStats?.costUsd || 0,
          timestamp: new Date().toISOString(),
        });
        await loadUsageStats();
      } else {
        const errorMsg = res.error || '알 수 없는 오류가 발생했습니다.';
        console.error('❌ YouTube 스크립트 생성 실패:', errorMsg);
        setError(`유튜브 스크립트 생성 실패: ${errorMsg}`);
      }
    } catch (e: any) {
      console.error('❌ YouTube 스크립트 생성 예외:', e);
      const errorMsg = e?.response?.data?.error || e?.message || '오류가 발생했습니다.';
      setError(`유튜브 스크립트 생성 실패: ${errorMsg}`);
      
      // 네트워크 오류인 경우
      if (e?.code === 'ECONNABORTED' || e?.message?.includes('timeout')) {
        setError('요청 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.');
      }
      
      // 401, 403 오류인 경우
      if (e?.response?.status === 401) {
        setError('로그인이 필요합니다. 페이지를 새로고침해주세요.');
      } else if (e?.response?.status === 403) {
        setError('접근 권한이 없습니다.');
      }
    } finally {
      setLoading(null);
    }
  };

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '2rem' }}>
      <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 16 }}>AI 콘텐츠 생성 엔진</h1>

      {/* 사용량 통계 제거됨 */}

      <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
        {/* 포커스 키워드 입력 (한 줄) */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label htmlFor="focusKw" style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: 4, display: 'block' }}>
              포커스 키워드
            </label>
            <input
              id="focusKw"
              type="text"
              value={focusKeyword}
              onChange={(e) => setFocusKeyword(e.target.value)}
              placeholder="예: 디지털 노마드"
              style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: '0.875rem', width: '100%' }}
            />
          </div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end' }}>
            <input
              type="number"
              min="1"
              max="20"
              value={focusKeywordCount}
              onChange={(e) => setFocusKeywordCount(Math.max(1, Math.min(20, parseInt(e.target.value) || 5)))}
              style={{ 
                width: '60px', 
                padding: '8px 6px', 
                borderRadius: 8, 
                border: '1px solid #ddd', 
                fontSize: '0.875rem',
                textAlign: 'center'
              }}
            />
            <button
              onClick={async () => {
                if (!title.trim()) {
                  setError('제목을 입력해주세요.');
                  return;
                }
                try {
                  setLoading('yt');
                  const response = await fetch('/api/blog/generate_focus_keywords.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'same-origin',
                    body: JSON.stringify({
                      title: title,
                      count: focusKeywordCount
                    })
                  });
                  const data = await response.json();
                  if (data.success && data.keywords && data.keywords.length > 0) {
                    // 생성된 포커스 키워드를 쉼표로 구분하여 포커스 키워드 필드에 추가
                    const newKeywords = data.keywords.join(', ');
                    setFocusKeyword(prev => prev ? `${prev}, ${newKeywords}` : newKeywords);
                  } else {
                    setError(data.message || '포커스 키워드 생성에 실패했습니다.');
                  }
                } catch (err: any) {
                  setError('포커스 키워드 생성 중 오류가 발생했습니다: ' + err.message);
                } finally {
                  setLoading(null);
                }
              }}
              disabled={loading !== null}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: 'none',
                background: '#42a5f5',
                color: 'white',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: loading !== null ? 'not-allowed' : 'pointer',
                opacity: loading !== null ? 0.6 : 1,
                whiteSpace: 'nowrap'
              }}
            >
              생성
            </button>
          </div>
        </div>

        {/* 키워드 입력 (한 줄) */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label htmlFor="kw" style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: 4, display: 'block' }}>
              키워드
            </label>
            <input
              id="kw"
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="예: 디지털 노마드"
              style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: '0.875rem', width: '100%' }}
            />
          </div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end' }}>
            <input
              type="number"
              min="1"
              max="20"
              value={keywordCount}
              onChange={(e) => setKeywordCount(Math.max(1, Math.min(20, parseInt(e.target.value) || 5)))}
              style={{ 
                width: '60px', 
                padding: '8px 6px', 
                borderRadius: 8, 
                border: '1px solid #ddd', 
                fontSize: '0.875rem',
                textAlign: 'center'
              }}
            />
            <button
              onClick={async () => {
                if (!focusKeyword.trim() && !title.trim()) {
                  setError('포커스 키워드 또는 제목을 입력해주세요.');
                  return;
                }
                try {
                  setLoading('yt');
                  const response = await fetch('/api/blog/generate_keywords.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'same-origin',
                    body: JSON.stringify({
                      title: title || focusKeyword,
                      focusKeyword: focusKeyword || keyword,
                      count: keywordCount
                    })
                  });
                  const data = await response.json();
                  if (data.success && data.keywords && data.keywords.length > 0) {
                    // 생성된 키워드를 쉼표로 구분하여 키워드 필드에 추가
                    const newKeywords = data.keywords.join(', ');
                    setKeyword(prev => prev ? `${prev}, ${newKeywords}` : newKeywords);
                  } else {
                    setError(data.message || '키워드 생성에 실패했습니다.');
                  }
                } catch (err: any) {
                  setError('키워드 생성 중 오류가 발생했습니다: ' + err.message);
                } finally {
                  setLoading(null);
                }
              }}
              disabled={loading !== null}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: 'none',
                background: '#42a5f5',
                color: 'white',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: loading !== null ? 'not-allowed' : 'pointer',
                opacity: loading !== null ? 0.6 : 1,
                whiteSpace: 'nowrap'
              }}
            >
              생성
            </button>
          </div>
        </div>
        
        {/* 제목 입력 (선택사항) */}
        <label htmlFor="title" style={{ fontWeight: 600, marginTop: 8 }}>
          제목 <span style={{ fontSize: '0.875rem', color: '#666', fontWeight: 400 }}>(선택사항)</span>
        </label>
        <input
          id="title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="예: 디지털 노마드가 되기 위한 완벽 가이드"
          style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd' }}
        />
        
        {/* 원하는 컨텐츠 방향 서술 */}
        <label htmlFor="contentDirection" style={{ fontWeight: 600, marginTop: 8 }}>
          원하는 컨텐츠 방향 <span style={{ fontSize: '0.875rem', color: '#666', fontWeight: 400 }}>(선택사항)</span>
        </label>
        <textarea
          id="contentDirection"
          value={contentDirection}
          onChange={(e) => setContentDirection(e.target.value)}
          placeholder="예: 초보자를 위한 단계별 설명, 실제 경험담 중심, 실용적인 팁 강조 등..."
          rows={3}
          style={{ 
            padding: '10px 12px', 
            borderRadius: 8, 
            border: '1px solid #ddd',
            fontFamily: 'inherit',
            resize: 'vertical',
            minHeight: '80px'
          }}
        />
        <small style={{ fontSize: '0.875rem', color: '#666', marginTop: '-8px' }}>
          컨텐츠의 방향성이나 원하는 강조점을 자유롭게 작성해주세요.
        </small>
        
        {/* 생성 버튼 - 모던 디자인 */}
        {contentType === 'youtube' ? (
          <button 
            onClick={runYouTube} 
            disabled={loading !== null || !keyword.trim()} 
            className="content-generate-btn content-generate-btn--youtube"
            style={{
              width: '100%',
              padding: '16px 24px',
              fontSize: '16px',
              fontWeight: 700,
              color: '#fff',
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
              border: 'none',
              borderRadius: '12px',
              cursor: (!keyword.trim() || loading !== null) ? 'not-allowed' : 'pointer',
              opacity: (!keyword.trim() || loading !== null) ? 0.6 : 1,
              minHeight: '56px',
              boxShadow: (!keyword.trim() || loading !== null) ? 'none' : '0 4px 16px rgba(255, 107, 107, 0.4)',
              transition: 'all 0.3s ease',
              marginTop: '16px',
              marginBottom: '16px'
            }}
            onMouseEnter={(e) => {
              if (keyword.trim() && loading === null) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 107, 107, 0.5)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = (!keyword.trim() || loading !== null) ? 'none' : '0 4px 16px rgba(255, 107, 107, 0.4)';
            }}
          >
            {loading === 'yt' ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span>
                스크립트 생성 중…
              </span>
            ) : (
              '🎬 유튜브 스크립트 생성'
            )}
          </button>
        ) : (
          <div style={{ 
            display: 'flex', 
            gap: 12, 
            marginTop: 16,
            marginBottom: 16
          }}>
            <button 
              onClick={runBlog} 
              disabled={loading !== null || !keyword.trim()} 
              className="content-generate-btn"
              style={{
                flex: 1,
                fontSize: '16px',
                padding: '14px 24px',
                opacity: (!keyword.trim() || loading !== null) ? 0.6 : 1,
                cursor: (!keyword.trim() || loading !== null) ? 'not-allowed' : 'pointer',
                minHeight: '50px'
              }}
            >
              {loading === 'blog' ? '⏳ 생성 중…' : '✍️ 블로그 글쓰기'}
            </button>
          </div>
        )}

        {/* YouTube 스크립트 옵션 제거됨 - 사이드바에서 설정 */}
      </div>

      {error && (
        <div style={{ 
          padding: 16, 
          borderRadius: 8, 
          border: '2px solid #dc3545', 
          background: '#f8d7da', 
          color: '#842029', 
          marginBottom: 16,
          position: 'relative'
        }}>
          <div style={{ fontWeight: 600, marginBottom: 8, fontSize: '1rem' }}>
            ⚠️ 오류 발생
          </div>
          <div style={{ fontSize: '0.9rem', lineHeight: '1.5' }}>
            {error}
          </div>
          {error.includes('API 키') && (
            <div style={{ marginTop: 12, padding: 12, background: '#fff', borderRadius: 4, border: '1px solid #842029' }}>
              <div style={{ fontWeight: 600, marginBottom: 4, fontSize: '0.85rem' }}>💡 해결 방법:</div>
              <div style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
                1. <a href="/pages/profile.php" target="_blank" style={{ color: '#667eea', textDecoration: 'underline' }}>프로필 페이지</a>로 이동<br/>
                2. Claude API 키 섹션에서 API 키 입력<br/>
                3. "활성화" 체크박스 선택<br/>
                4. 저장 버튼 클릭
              </div>
            </div>
          )}
          <button
            onClick={() => setError('')}
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              background: 'none',
              border: 'none',
              fontSize: '1.2rem',
              cursor: 'pointer',
              color: '#842029',
              padding: '4px 8px'
            }}
            title="닫기"
          >
            ×
          </button>
        </div>
      )}

      {blogHtml && blogMetadata && (
        <section style={card()}>
          {/* 메타데이터 프리뷰 */}
          <div style={{
            background: '#e3f2fd',
            borderRadius: 8,
            padding: '1rem',
            marginBottom: '1rem',
            border: '1px solid #90caf9'
          }}>
            <h3 style={{ marginTop: 0, fontSize: '1rem', fontWeight: 600 }}>SEO 메타데이터</h3>
            <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.875rem' }}>
              <div>
                <strong>제목:</strong> {blogMetadata.title}
              </div>
              {blogMetadata.metadata && (
                <>
                  {blogMetadata.metadata.metaTitle && (
                    <div>
                      <strong>메타 제목:</strong> {String(blogMetadata.metadata.metaTitle)}
                    </div>
                  )}
                  {blogMetadata.metadata.metaDescription && (
                    <div>
                      <strong>메타 설명:</strong> {String(blogMetadata.metadata.metaDescription)}
                    </div>
                  )}
                  {blogMetadata.metadata.slug && (
                    <div>
                      <strong>URL 슬러그:</strong> {String(blogMetadata.metadata.slug)}
                    </div>
                  )}
                  {blogMetadata.metadata.seoScore !== undefined && (
                    <div>
                      <strong>SEO 점수:</strong> {String(blogMetadata.metadata.seoScore)}/100
                    </div>
                  )}
                </>
              )}
              {blogMetadata.generationStats && (
                <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #90caf9' }}>
                  <strong>생성 통계:</strong> {blogMetadata.generationStats.generationTime.toFixed(2)}초, 
                  비용: ${blogMetadata.generationStats.costUsd.toFixed(4)}
                </div>
              )}
            </div>
          </div>

          {/* 팩트 체크 버튼 */}
          <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={runFactCheck}
              disabled={loadingFactCheck || !blogHtml}
              style={{
                padding: '0.5rem 1rem',
                background: loadingFactCheck ? '#ccc' : '#2196f3',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: loadingFactCheck ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: 600
              }}
            >
              {loadingFactCheck ? '팩트 체크 중…' : '🔍 팩트 체크 수행'}
            </button>
          </div>

          {/* 팩트 체크 결과 */}
          {factCheckResult && (
            <div style={{ marginBottom: '1.5rem' }}>
              <FactCheckResult result={factCheckResult} />
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ marginTop: 0 }}>블로그 초안</h2>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setShowPublishPanel(!showPublishPanel)}
                style={{
                  padding: '0.5rem 1rem',
                  background: showPublishPanel ? '#42a5f5' : '#f0f0f0',
                  color: showPublishPanel ? '#fff' : '#333',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                {showPublishPanel ? '발행 닫기' : '🚀 멀티 발행'}
              </button>
              <button
                onClick={() => setShowPublishHistory(!showPublishHistory)}
                style={{
                  padding: '0.5rem 1rem',
                  background: showPublishHistory ? '#00b894' : '#f0f0f0',
                  color: showPublishHistory ? '#fff' : '#333',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                발행 이력
              </button>
              <button
                onClick={() => setShowTemplateModal(true)}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#9c27b0',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                디자인 템플릿
              </button>
              <button
                onClick={() => {
                  const editor = document.getElementById('blog-editor');
                  if (editor) editor.scrollIntoView({ behavior: 'smooth' });
                }}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#42a5f5',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                에디터에서 열기
              </button>
            </div>
          </div>
          
          {/* Project 8: 멀티 발행 패널 */}
          {showPublishPanel && blogMetadata && (
            <div style={{
              background: '#fff',
              borderRadius: 8,
              padding: '1.5rem',
              marginBottom: '1rem',
              border: '1px solid #ddd'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600 }}>
                  🚀 멀티 플랫폼 발행
                </h3>
                <button
                  onClick={() => {
                    setTokenModalPlatform(null);
                    setShowTokenModal(true);
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#f0f0f0',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  플랫폼 연결 관리
                </button>
              </div>

              <PublishingTargets
                contentId={blogMetadata.id}
                onPublishComplete={(results) => {
                  // jobIds 추출
                  const jobIds: Record<PublishPlatform, string> = {} as Record<PublishPlatform, string>;
                  Object.entries(results).forEach(([platform, result]: [string, any]) => {
                    if (result.success && result.jobId) {
                      jobIds[platform as PublishPlatform] = result.jobId;
                    }
                  });

                  if (Object.keys(jobIds).length > 0) {
                    setPublishJobIds(jobIds);
                    setShowPublishStatusModal(true);
                  }
                }}
                onTokenModalOpen={(platform) => {
                  setTokenModalPlatform(platform);
                  setShowTokenModal(true);
                }}
              />
            </div>
          )}

          {/* 발행 이력 */}
          {showPublishHistory && (
            <div style={{
              background: '#fff',
              borderRadius: 8,
              padding: '1.5rem',
              marginBottom: '1rem',
              border: '1px solid #ddd'
            }}>
              <PublishHistory />
            </div>
          )}

          {/* 토큰 관리 모달 */}
          <TokenModal
            platform={tokenModalPlatform || 'wordpress'}
            isOpen={showTokenModal}
            onClose={() => {
              setShowTokenModal(false);
              setTokenModalPlatform(null);
            }}
            onSuccess={() => {
              // 플랫폼 목록 새로고침은 PublishingTargets에서 처리됨
            }}
          />

          {/* 발행 상태 모달 */}
          <PublishStatusModal
            jobIds={publishJobIds}
            isOpen={showPublishStatusModal}
            onClose={() => setShowPublishStatusModal(false)}
          />
                  <div 
                    id="blog-preview"
                    className={selectedTemplate ? selectedTemplate.css_class : ''}
                    style={{
                      background: selectedTemplate ? 'transparent' : '#f6f8fa',
                      borderRadius: 8,
                      padding: '1rem',
                      maxHeight: 400,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      fontFamily: selectedTemplate ? 'inherit' : 'monospace',
                      fontSize: selectedTemplate ? 'inherit' : '0.875rem'
                    }}
                    dangerouslySetInnerHTML={{ __html: blogHtml }}
                  />
                  {/* 피드백 섹션 */}
                  {blogMetadata?.id && (
                    <div style={{
                      marginTop: '1rem',
                      padding: '1rem',
                      background: '#fff3cd',
                      borderRadius: 8,
                      border: '1px solid #ffc107'
                    }}>
                      <h3 style={{ marginTop: 0, fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                        이 콘텐츠에 대한 피드백을 남겨주세요
                      </h3>
                      {/* TODO: FeedbackForm 컴포넌트 구현 필요 */}
                      <div>피드백 기능은 준비 중입니다.</div>
                    </div>
                  )}

                  <div id="blog-editor" style={{ marginTop: '1.5rem' }}>
                    <ContentEditor
                      initialContent={blogHtml}
                      contentType="blog"
                      keyword={keyword}
                      onContentChange={(newContent) => setBlogHtml(newContent)}
                      onSave={async (content, metadata) => {
                        try {
                          // 다운로드
                          const blob = new Blob([content], { type: 'text/markdown' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `blog-${keyword}-${Date.now()}.md`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                          
                          // 클립보드 복사
                          await navigator.clipboard.writeText(content);
                          alert('저장 및 클립보드 복사 완료!');
                        } catch (e) {
                          console.error('저장 실패:', e);
                          alert('저장 중 오류가 발생했습니다.');
                        }
                      }}
                    />
                  </div>
        </section>
      )}

      {ytScript && (
        <section style={card()}>
          {/* 유튜브 최적화 메타데이터 */}
          {ytOptimization && (
            <div style={{
              background: '#e3f2fd',
              borderRadius: 8,
              padding: '1rem',
              marginBottom: '1rem',
              border: '1px solid #90caf9'
            }}>
              <h3 style={{ marginTop: 0, fontSize: '1rem', fontWeight: 600 }}>유튜브 최적화 메타데이터</h3>
              
              {/* 제목 제안 */}
              {ytOptimization.titles.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong style={{ fontSize: '0.875rem' }}>제목 제안:</strong>
                  <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {ytOptimization.titles.map((title, idx) => (
                      <div key={idx} style={{
                        padding: '0.5rem',
                        background: '#fff',
                        borderRadius: 4,
                        fontSize: '0.875rem',
                        border: idx === 0 ? '2px solid #42a5f5' : '1px solid #ddd'
                      }}>
                        {idx === 0 && <span style={{ color: '#42a5f5', marginRight: '0.5rem' }}>⭐</span>}
                        {title}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 설명란 */}
              {ytOptimization.description && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong style={{ fontSize: '0.875rem' }}>설명란:</strong>
                  <textarea
                    readOnly
                    value={ytOptimization.description}
                    style={{
                      width: '100%',
                      minHeight: '100px',
                      padding: '0.5rem',
                      marginTop: '0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: 4,
                      fontSize: '0.875rem',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>
              )}

              {/* 태그 */}
              {ytOptimization.tags.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong style={{ fontSize: '0.875rem' }}>태그:</strong>
                  <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                    {ytOptimization.tags.map((tag, idx) => (
                      <span key={idx} style={{
                        padding: '0.25rem 0.5rem',
                        background: '#fff',
                        borderRadius: 4,
                        fontSize: '0.75rem',
                        border: '1px solid #ddd'
                      }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* 챕터 */}
              {ytOptimization.chapters.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong style={{ fontSize: '0.875rem' }}>챕터:</strong>
                  <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {ytOptimization.chapters.map((chapter, idx) => (
                      <div key={idx} style={{
                        padding: '0.5rem',
                        background: '#fff',
                        borderRadius: 4,
                        fontSize: '0.875rem',
                        border: '1px solid #ddd'
                      }}>
                        <strong>{chapter.time}</strong> - {chapter.title}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 썸네일 아이디어 */}
              {ytOptimization.thumbnailIdeas.length > 0 && (
                <div>
                  <strong style={{ fontSize: '0.875rem' }}>썸네일 아이디어:</strong>
                  <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {ytOptimization.thumbnailIdeas.map((idea, idx) => (
                      <div key={idx} style={{
                        padding: '0.75rem',
                        background: '#fff',
                        borderRadius: 4,
                        border: '1px solid #ddd',
                        fontSize: '0.875rem'
                      }}>
                        <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{idea.title}</div>
                        <div style={{ color: '#666', fontSize: '0.8125rem' }}>{idea.description}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
            <h2 style={{ marginTop: 0, fontSize: '1.5rem', fontWeight: 700, color: '#2c3e50' }}>
              📝 유튜브 스크립트
            </h2>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                onClick={() => setYtActiveTab('editor')}
                style={{
                  padding: '0.625rem 1.25rem',
                  background: '#42a5f5',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 4px rgba(66, 165, 245, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#1976d2';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#42a5f5';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                ✏️ 에디터에서 열기
              </button>
              <button
                onClick={() => {
                  const blob = new Blob([ytScript], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `youtube-script-${keyword}-${Date.now()}.txt`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
                style={{
                  padding: '0.625rem 1.25rem',
                  background: '#6b7280',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#4b5563';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#6b7280';
                }}
              >
                💾 다운로드
              </button>
              <button
                onClick={() => setShowTeleprompter(true)}
                style={{
                  padding: '0.625rem 1.25rem',
                  background: '#9c27b0',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#7b1fa2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#9c27b0';
                }}
              >
                📺 프롬프터
              </button>
            </div>
          </div>

          {/* 탭 */}
          <div style={{ 
            display: 'flex', 
            gap: '0.5rem', 
            borderBottom: '2px solid #e9ecef', 
            marginBottom: '1rem',
            overflowX: 'auto',
            flexWrap: 'nowrap'
          }}>
            <button
              onClick={() => setYtActiveTab('script')}
              style={{
                padding: '0.75rem 1.5rem',
                background: ytActiveTab === 'script' ? '#f8f9fa' : 'transparent',
                border: 'none',
                borderBottom: ytActiveTab === 'script' ? '3px solid #42a5f5' : '3px solid transparent',
                color: ytActiveTab === 'script' ? '#42a5f5' : '#6b7280',
                fontWeight: ytActiveTab === 'script' ? 600 : 400,
                cursor: 'pointer',
                fontSize: '0.875rem',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap'
              }}
            >
              📄 스크립트
            </button>
            <button
              onClick={() => setYtActiveTab('editor')}
              style={{
                padding: '0.75rem 1.5rem',
                background: ytActiveTab === 'editor' ? '#f8f9fa' : 'transparent',
                border: 'none',
                borderBottom: ytActiveTab === 'editor' ? '3px solid #42a5f5' : '3px solid transparent',
                color: ytActiveTab === 'editor' ? '#42a5f5' : '#6b7280',
                fontWeight: ytActiveTab === 'editor' ? 600 : 400,
                cursor: 'pointer',
                fontSize: '0.875rem',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap'
              }}
            >
              ✏️ 에디터
            </button>
            <button
              onClick={() => setYtActiveTab('timeline')}
              style={{
                padding: '0.75rem 1.5rem',
                background: ytActiveTab === 'timeline' ? '#f8f9fa' : 'transparent',
                border: 'none',
                borderBottom: ytActiveTab === 'timeline' ? '3px solid #42a5f5' : '3px solid transparent',
                color: ytActiveTab === 'timeline' ? '#42a5f5' : '#6b7280',
                fontWeight: ytActiveTab === 'timeline' ? 600 : 400,
                cursor: 'pointer',
                fontSize: '0.875rem',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap'
              }}
            >
              ⏱️ 타임라인
            </button>
            <button
              onClick={() => setYtActiveTab('seo')}
              style={{
                padding: '0.75rem 1.5rem',
                background: ytActiveTab === 'seo' ? '#f8f9fa' : 'transparent',
                border: 'none',
                borderBottom: ytActiveTab === 'seo' ? '3px solid #42a5f5' : '3px solid transparent',
                color: ytActiveTab === 'seo' ? '#42a5f5' : '#6b7280',
                fontWeight: ytActiveTab === 'seo' ? 600 : 400,
                cursor: 'pointer',
                fontSize: '0.875rem',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap'
              }}
            >
              🎯 SEO
            </button>
          </div>

          {/* 스크립트 탭 */}
          {ytActiveTab === 'script' && (
            <div style={{
              background: '#f6f8fa',
              borderRadius: 8,
              padding: '1.5rem',
              maxHeight: '90vh',
              minHeight: '600px',
              height: 'auto',
              overflow: 'auto',
              fontFamily: '"Noto Sans KR", -apple-system, sans-serif',
              fontSize: '0.9375rem',
              lineHeight: '1.8',
              color: '#2c3e50',
              resize: 'vertical',
              border: '2px solid #e9ecef'
            }}>
              {formatScriptContent(ytScript)}
            </div>
          )}

          {/* 타임라인 탭 */}
          {ytActiveTab === 'timeline' && (
            <div style={{ 
              maxHeight: '90vh',
              minHeight: '600px',
              height: 'auto',
              overflow: 'auto',
              background: '#ffffff',
              borderRadius: 8,
              padding: '1.5rem',
              resize: 'vertical',
              border: '2px solid #e9ecef'
            }}>
              {ytTimeline.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  {ytTimeline.map((item, index) => (
                    <div key={index} style={{
                      background: '#f8f9fa',
                      borderLeft: '4px solid #42a5f5',
                      borderRadius: '0 8px 8px 0',
                      padding: '1.25rem',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#e3f2fd';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(66,165,245,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f8f9fa';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                    }}>
                      {/* 헤더: 타임스탬프 + 섹션명 + 길이 */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '1rem', 
                        marginBottom: '0.75rem',
                        flexWrap: 'wrap'
                      }}>
                        <span style={{
                          display: 'inline-block',
                          background: '#42a5f5',
                          color: 'white',
                          padding: '0.375rem 0.75rem',
                          borderRadius: '6px',
                          fontFamily: '"Courier New", monospace',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          letterSpacing: '0.5px'
                        }}>
                          🕐 {item.timestamp}
                        </span>
                        <span style={{ 
                          fontWeight: 700, 
                          fontSize: '1.0625rem',
                          color: '#2c3e50',
                          flex: 1
                        }}>
                          {item.section}
                        </span>
                        <span style={{ 
                          fontSize: '0.8125rem', 
                          color: '#7f8c8d',
                          background: 'white',
                          padding: '0.25rem 0.625rem',
                          borderRadius: '4px',
                          border: '1px solid #dee2e6'
                        }}>
                          ⏱️ {item.duration}초
                        </span>
                      </div>
                      {/* 내용 */}
                      <div style={{ 
                        color: '#495057', 
                        margin: 0, 
                        lineHeight: 1.8,
                        fontSize: '0.9375rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'keep-all'
                      }}>
                        {item.content}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '4rem 2rem', 
                  color: '#adb5bd',
                  fontSize: '1rem'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📝</div>
                  <div>타임라인 데이터가 없습니다.</div>
                  <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                    스크립트를 생성하면 자동으로 타임라인이 생성됩니다.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* SEO 탭 */}
          {ytActiveTab === 'seo' && (
            <div style={{ 
              maxHeight: '90vh',
              minHeight: '600px',
              height: 'auto',
              overflow: 'auto',
              background: '#ffffff',
              borderRadius: 8,
              padding: '1.5rem',
              resize: 'vertical',
              border: '2px solid #e9ecef'
            }}>
              {ytOptimization ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {/* 제목 제안 */}
              {ytOptimization.titles.length > 0 && (
                <div>
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem' }}>제목 제안 (A/B 테스트용)</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {ytOptimization.titles.map((title, idx) => (
                      <div key={idx} style={{
                        padding: '0.75rem',
                        background: idx === 0 ? '#e3f2fd' : '#f8f9fa',
                        borderRadius: 6,
                        fontSize: '0.875rem',
                        border: idx === 0 ? '2px solid #42a5f5' : '1px solid #ddd'
                      }}>
                        <span style={{ color: '#42a5f5', marginRight: '0.5rem', fontWeight: 600 }}>
                          버전 {String.fromCharCode(65 + idx)}:
                        </span>
                        {title}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 설명 */}
              {ytOptimization.description && (
                <div>
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem' }}>설명</h3>
                  <pre style={{
                    background: '#f8f9fa',
                    padding: '1rem',
                    borderRadius: 6,
                    whiteSpace: 'pre-wrap',
                    fontSize: '0.875rem',
                    border: '1px solid #ddd'
                  }}>
                    {ytOptimization.description}
                  </pre>
                </div>
              )}

              {/* 태그 */}
              {ytOptimization.tags && ytOptimization.tags.length > 0 && (
                <div>
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                    태그 ({ytOptimization.tags.length}개)
                  </h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {ytOptimization.tags.slice(0, 50).map((tag, idx) => (
                      <span key={idx} style={{
                        padding: '0.25rem 0.5rem',
                        background: '#fff',
                        border: '1px solid #ddd',
                        borderRadius: 4,
                        fontSize: '0.75rem'
                      }}>
                        {tag}
                      </span>
                    ))}
                    {ytOptimization.tags.length > 50 && (
                      <span style={{ padding: '0.25rem 0.5rem', color: '#999', fontSize: '0.75rem' }}>
                        +{ytOptimization.tags.length - 50}개 더
                      </span>
                    )}
                  </div>
                </div>
              )}
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '4rem 2rem', 
                  color: '#adb5bd',
                  fontSize: '1rem'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎯</div>
                  <div>SEO 최적화 데이터가 없습니다.</div>
                  <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                    스크립트를 생성하면 자동으로 SEO 최적화가 생성됩니다.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 에디터 탭 */}
          {ytActiveTab === 'editor' && (
            <div id="yt-editor" style={{
              background: '#ffffff',
              borderRadius: 8,
              padding: '0',
              maxHeight: '90vh',
              minHeight: '600px',
              height: 'auto',
              overflow: 'hidden',
              border: '2px solid #e9ecef',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* 에디터 모드 전환 버튼 */}
              <div style={{
                padding: '1rem 1.5rem',
                borderBottom: '1px solid #e9ecef',
                background: '#f8f9fa',
                display: 'flex',
                gap: '0.75rem',
                alignItems: 'center'
              }}>
                <button
                  onClick={() => setYtEditorMode('preview')}
                  style={{
                    padding: '0.5rem 1rem',
                    background: ytEditorMode === 'preview' ? '#42a5f5' : '#e9ecef',
                    color: ytEditorMode === 'preview' ? '#ffffff' : '#495057',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    transition: 'all 0.2s'
                  }}
                >
                  👁️ 미리보기
                </button>
                <button
                  onClick={() => setYtEditorMode('edit')}
                  style={{
                    padding: '0.5rem 1rem',
                    background: ytEditorMode === 'edit' ? '#42a5f5' : '#e9ecef',
                    color: ytEditorMode === 'edit' ? '#ffffff' : '#495057',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    transition: 'all 0.2s'
                  }}
                >
                  ✏️ 편집
                </button>
                <button
                  onClick={() => setYtEditorMode('source')}
                  style={{
                    padding: '0.5rem 1rem',
                    background: ytEditorMode === 'source' ? '#42a5f5' : '#e9ecef',
                    color: ytEditorMode === 'source' ? '#ffffff' : '#495057',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    transition: 'all 0.2s'
                  }}
                >
                  💻 소스보기
                </button>
                <div style={{ flex: 1 }}></div>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(ytScript);
                      alert('📋 클립보드에 복사되었습니다!');
                    } catch (e) {
                      console.error('복사 실패:', e);
                      alert('❌ 복사 중 오류가 발생했습니다.');
                    }
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#6b7280',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    transition: 'all 0.2s'
                  }}
                >
                  📋 복사
                </button>
                <button
                  onClick={() => {
                    try {
                      const blob = new Blob([ytScript], { type: 'text/plain;charset=utf-8' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `youtube-script-${keyword}-${Date.now()}.txt`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                      alert('💾 스크립트가 다운로드되었습니다!');
                    } catch (error) {
                      console.error('다운로드 실패:', error);
                      alert('❌ 다운로드 중 오류가 발생했습니다.');
                    }
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#42a5f5',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    transition: 'all 0.2s'
                  }}
                >
                  💾 다운로드
                </button>
              </div>

              {/* 미리보기 모드 */}
              {ytEditorMode === 'preview' && (
                <div style={{
                  flex: 1,
                  overflow: 'auto',
                  background: '#f6f8fa',
                  padding: '1.5rem',
                  fontFamily: '"Noto Sans KR", -apple-system, sans-serif',
                  fontSize: '0.9375rem',
                  lineHeight: '1.8',
                  color: '#2c3e50'
                }}>
                  {formatScriptContent(ytScript)}
                </div>
              )}

              {/* 편집 모드 */}
              {ytEditorMode === 'edit' && (
                <div style={{ flex: 1, overflow: 'auto' }}>
                  <ContentEditor
                    initialContent={ytScript}
                    contentType="youtube"
                    keyword={keyword}
                    onContentChange={(newContent) => {
                      setYtScript(newContent);
                      saveScriptToCacheDebounced(newContent, keyword, ytTimeline);
                    }}
                    onSave={async (content, metadata) => {
                      try {
                        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `youtube-script-${keyword}-${Date.now()}.txt`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                        await navigator.clipboard.writeText(content);
                        alert('💾 저장 및 클립보드 복사 완료!');
                      } catch (e) {
                        console.error('저장 실패:', e);
                        alert('❌ 저장 중 오류가 발생했습니다.');
                      }
                    }}
                  />
                </div>
              )}

              {/* 소스보기 모드 */}
              {ytEditorMode === 'source' && (
                <div style={{ flex: 1, overflow: 'auto', background: '#282c34', padding: '1.5rem' }}>
                  <textarea
                    value={ytScript}
                    onChange={(e) => {
                      setYtScript(e.target.value);
                      saveScriptToCacheDebounced(e.target.value, keyword, ytTimeline);
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      minHeight: '500px',
                      background: 'transparent',
                      color: '#abb2bf',
                      border: 'none',
                      outline: 'none',
                      fontFamily: '"Fira Code", "Courier New", monospace',
                      fontSize: '0.875rem',
                      lineHeight: '1.6',
                      padding: 0,
                      resize: 'none',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                    spellCheck={false}
                  />
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* 디자인 템플릿 모달 */}
      {showTemplateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            padding: '2rem',
            borderRadius: 12,
            boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
            width: '90%',
            maxWidth: '900px',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: 700 }}>
              디자인 템플릿 선택
            </h2>
            
            {/* 카테고리 필터 */}
            <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                onClick={() => setTemplateCategory('all')}
                style={{
                  padding: '0.5rem 1rem',
                  background: templateCategory === 'all' ? '#9c27b0' : '#f0f0f0',
                  color: templateCategory === 'all' ? '#fff' : '#333',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                전체
              </button>
              {['layout', 'theme', 'color', 'typography'].map(cat => (
                <button
                  key={cat}
                  onClick={() => setTemplateCategory(cat)}
                  style={{
                    padding: '0.5rem 1rem',
                    background: templateCategory === cat ? '#9c27b0' : '#f0f0f0',
                    color: templateCategory === cat ? '#fff' : '#333',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  {cat === 'layout' ? '레이아웃' : cat === 'theme' ? '테마' : cat === 'color' ? '컬러' : '타이포그래피'}
                </button>
              ))}
            </div>

            {/* 템플릿 목록 */}
            {designTemplates.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#666' }}>템플릿을 불러오는 중...</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1rem' }}>
                {designTemplates
                  .filter(t => templateCategory === 'all' || t.category === templateCategory)
                  .map(template => (
                    <div
                      key={template.template_id}
                      onClick={async () => {
                        setSelectedTemplate(template);
                        await designTemplateService.incrementUsage(template.template_id);
                        setShowTemplateModal(false);
                      }}
                      style={{
                        border: selectedTemplate?.template_id === template.template_id ? '2px solid #9c27b0' : '1px solid #ddd',
                        borderRadius: 8,
                        padding: '1rem',
                        cursor: 'pointer',
                        background: selectedTemplate?.template_id === template.template_id ? '#f3e5f5' : '#fff',
                        transition: 'all 0.2s'
                      }}
                    >
                      <h3 style={{ marginTop: 0, fontSize: '1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                        {template.template_name}
                        {template.is_premium && (
                          <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#ff9800' }}>⭐</span>
                        )}
                      </h3>
                      <p style={{ fontSize: '0.875rem', color: '#666', margin: '0.5rem 0' }}>
                        {template.description}
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.5rem' }}>
                        {template.tags.map(tag => (
                          <span
                            key={tag}
                            style={{
                              padding: '0.25rem 0.5rem',
                              background: '#f0f0f0',
                              borderRadius: 4,
                              fontSize: '0.75rem'
                            }}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.5rem' }}>
                        사용 횟수: {template.usage_count}
                      </div>
                    </div>
                  ))}
              </div>
            )}

            <button
              onClick={() => setShowTemplateModal(false)}
              style={{
                marginTop: '1.5rem',
                width: '100%',
                padding: '0.75rem',
                background: '#e0e0e0',
                color: '#333',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer'
              }}
            >
              닫기
            </button>
          </div>
        </div>
      )}

      {/* Preset Modals */}
      {showSavePresetModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            padding: '2rem',
            borderRadius: 12,
            boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
            width: '90%',
            maxWidth: '500px'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: 700 }}>
              프리셋 저장
            </h2>
            <SavePresetForm
              blogOptions={blogOptions}
              onSave={async (name, description, tags) => {
                try {
                  await presetService.save({
                    preset_name: name,
                    description: description,
                    blog_options: blogOptions,
                    tags: tags,
                    user_id: 1 // TODO: 실제 사용자 ID로 교체
                  });
                  alert('프리셋이 성공적으로 저장되었습니다.');
                  setShowSavePresetModal(false);
                  loadPresets(); // 프리셋 목록 새로고침
                } catch (e: any) {
                  alert('프리셋 저장 실패: ' + (e.message || '알 수 없는 오류'));
                }
              }}
              onCancel={() => setShowSavePresetModal(false)}
            />
          </div>
        </div>
      )}

      {showPresetModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            padding: '2rem',
            borderRadius: 12,
            boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: 700 }}>
              저장된 프리셋 불러오기
            </h2>
            {savedPresets.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#666' }}>저장된 프리셋이 없습니다.</p>
            ) : (
              <div style={{ display: 'grid', gap: '1rem' }}>
                {savedPresets.map(preset => (
                  <div
                    key={preset.id}
                    style={{
                      border: '1px solid #eee',
                      borderRadius: 8,
                      padding: '1rem',
                      cursor: 'pointer',
                      background: blogOptions.contentType === preset.blog_options.contentType &&
                                  blogOptions.targetLength === preset.blog_options.targetLength &&
                                  blogOptions.tone === preset.blog_options.tone &&
                                  blogOptions.targetAudience === preset.blog_options.targetAudience &&
                                  blogOptions.emojiUsage === preset.blog_options.emojiUsage &&
                                  blogOptions.emoticonsAllowed === preset.blog_options.emoticonsAllowed &&
                                  blogOptions.visualExpression === preset.blog_options.visualExpression
                                  ? '#e3f2fd' : '#fff'
                    }}
                    onClick={() => {
                      setBlogOptions(preset.blog_options);
                      setSelectedPersonality(PERSONALITY_PRESETS.find(p => p.id === preset.personality_id) || null);
                      setPriority(preset.priority || 'balance');
                      setShowPresetModal(false);
                    }}
                  >
                    <h3 style={{ marginTop: 0, fontSize: '1.1rem', fontWeight: 600 }}>{preset.preset_name}</h3>
                    <p style={{ fontSize: '0.875rem', color: '#666', margin: '0.5rem 0' }}>{preset.description}</p>
                    <div style={{ fontSize: '0.8rem', color: '#999' }}>
                      {preset.tags?.map(tag => <span key={tag} style={{ marginRight: '0.5rem', background: '#f0f0f0', padding: '0.2rem 0.5rem', borderRadius: 4 }}>#{tag}</span>)}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#aaa', marginTop: '0.5rem' }}>
                      사용 횟수: {preset.usage_count} | 마지막 사용: {new Date(preset.last_used_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={() => setShowPresetModal(false)}
              style={{
                marginTop: '1.5rem',
                width: '100%',
                padding: '0.75rem',
                background: '#e0e0e0',
                color: '#333',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer'
              }}
            >
              닫기
            </button>
          </div>
        </div>
      )}

      {/* 프롬프터 모드 모달 */}
      {showTeleprompter && ytScript && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: '#000',
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem'
        }}>
          <div style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            zIndex: 2001
          }}>
            <button
              onClick={() => setShowTeleprompter(false)}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#fff',
                color: '#000',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 600
              }}
            >
              닫기 (ESC)
            </button>
          </div>
          <div
            id="teleprompter-content"
            style={{
              color: '#fff',
              fontSize: '3rem',
              lineHeight: 1.8,
              textAlign: 'center',
              maxWidth: '1200px',
              fontWeight: 400,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            {ytScript.split('\n').map((line, idx) => (
              <div key={idx} style={{ marginBottom: '1.5rem' }}>
                {line || '\u00A0'}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function btn(): React.CSSProperties {
  return {
    background: 'linear-gradient(135deg, #42a5f5 0%, #81d4fa 100%)',
    border: 'none',
    borderRadius: 8,
    padding: '10px 16px',
    fontWeight: 700,
    color: '#fff',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(66, 165, 245, 0.3)',
    transition: 'all 0.2s ease',
  };
}

function card(): React.CSSProperties {
  return {
    background: '#fff',
    border: '1px solid rgba(0,0,0,0.06)',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    boxShadow: '0 6px 16px rgba(66,165,245,0.10)',
  };
}

function pre(): React.CSSProperties {
  return {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    background: '#f6f8fa',
    borderRadius: 8,
    padding: 12,
    maxHeight: 420,
    overflow: 'auto',
    margin: 0,
  };
}

// 프리셋 저장 폼 컴포넌트
function SavePresetForm({ blogOptions, onSave, onCancel }: {
  blogOptions: BlogOptions;
  onSave: (name: string, description: string, tags: string[]) => void;
  onCancel: () => void;
}) {
  const [presetName, setPresetName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');

  const handleSave = () => {
    if (!presetName.trim()) {
      alert('프리셋 이름을 입력해주세요.');
      return;
    }

    const tagArray = tags.split(',').map(t => t.trim()).filter(t => t);
    onSave(presetName.trim(), description.trim(), tagArray);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div>
        <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>
          프리셋 이름 <span style={{ color: '#d63031' }}>*</span>
        </label>
        <input
          type="text"
          value={presetName}
          onChange={(e) => setPresetName(e.target.value)}
          placeholder="예: 친근한 정보성 블로그 스타일"
          maxLength={200}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid #ddd'
          }}
        />
      </div>

      <div>
        <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>
          설명 (선택사항)
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="이 프리셋의 특징이나 용도를 설명해주세요"
          maxLength={500}
          rows={3}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid #ddd',
            fontFamily: 'inherit'
          }}
        />
      </div>

      <div>
        <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>
          태그 (선택사항)
        </label>
        <input
          type="text"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="태그1, 태그2, 태그3"
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid #ddd'
          }}
        />
        <small style={{ color: '#666', fontSize: '0.75rem' }}>쉼표로 구분하여 입력</small>
      </div>

      <div style={{
        background: '#f8f9fa',
        padding: '1rem',
        borderRadius: 8,
        fontSize: '0.875rem',
        color: '#666'
      }}>
        <strong>저장될 설정:</strong>
        <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
          <li>콘텐츠 유형: {blogOptions.contentType}</li>
          <li>글 길이: {blogOptions.targetLength}자</li>
          <li>톤: {blogOptions.tone}</li>
          <li>대상: {blogOptions.targetAudience}</li>
          <li>이모지: {blogOptions.emojiUsage}</li>
        </ul>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button
          onClick={onCancel}
          style={{
            flex: 1,
            padding: '0.75rem',
            background: '#e0e0e0',
            color: '#333',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer'
          }}
        >
          취소
        </button>
        <button
          onClick={handleSave}
          disabled={!presetName.trim()}
          style={{
            flex: 1,
            padding: '0.75rem',
            background: presetName.trim() ? '#27ae60' : '#ccc',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            cursor: presetName.trim() ? 'pointer' : 'not-allowed',
            fontWeight: 600
          }}
        >
          저장하기
        </button>
      </div>
    </div>
  );
}

// ===========================
// localStorage 자동 저장/복원 유틸리티
// ===========================

/**
 * YouTube 옵션을 localStorage에 저장
 */
function saveYouTubeOptionsToLocalStorage(options: any) {
  try {
    const settings = {
      ...options,
      savedAt: new Date().toISOString()
    };
    localStorage.setItem('youtubeReactSettings', JSON.stringify(settings));
    console.log('💾 [React] 설정이 자동 저장되었습니다');
  } catch (e) {
    console.error('[React] localStorage 저장 실패:', e);
  }
}

/**
 * 스크립트를 캐시에 저장 (부모 페이지로 전송)
 */
let scriptSaveTimeout: number | null = null;
function saveScriptToCacheDebounced(script: string, keyword: string, timeline: any[]) {
  if (scriptSaveTimeout) {
    clearTimeout(scriptSaveTimeout);
  }
  
  scriptSaveTimeout = setTimeout(() => {
    window.parent.postMessage({
      type: 'SAVE_SCRIPT_TO_CACHE',
      script: script,
      keyword: keyword,
      timeline: timeline,
      metadata: {}
    }, window.location.origin);
  }, 1000); // 1초 디바운스
}

/**
 * localStorage에서 YouTube 옵션 복원
 */
function restoreYouTubeOptionsFromLocalStorage() {
  try {
    const savedSettings = localStorage.getItem('youtubeReactSettings');
    if (!savedSettings) {
      return null;
    }
    
    const settings = JSON.parse(savedSettings);
    
    // 7일 이상 지난 설정은 삭제
    const savedDate = new Date(settings.savedAt);
    const daysPassed = (new Date().getTime() - savedDate.getTime()) / (1000 * 60 * 60 * 24);
    if (daysPassed > 7) {
      localStorage.removeItem('youtubeReactSettings');
      return null;
    }
    
    console.log('📂 [React] 이전 설정 복원:', settings);
    return settings;
  } catch (e) {
    console.error('[React] localStorage 복원 실패:', e);
    return null;
  }
}

/**
 * 마크다운 텍스트를 HTML로 변환
 */
function parseMarkdown(text: string) {
  if (!text) return '';
  
  // # 제목
  text = text.replace(/^# (.+)$/gm, '<h1 style="font-size: 1.5rem; font-weight: 700; color: #1f2937; margin: 1.5rem 0 1rem 0;">$1</h1>');
  text = text.replace(/^## (.+)$/gm, '<h2 style="font-size: 1.25rem; font-weight: 600; color: #374151; margin: 1.25rem 0 0.75rem 0;">$1</h2>');
  text = text.replace(/^### (.+)$/gm, '<h3 style="font-size: 1.125rem; font-weight: 600; color: #4b5563; margin: 1rem 0 0.5rem 0;">$1</h3>');
  
  // **굵게**
  text = text.replace(/\*\*(.+?)\*\*/g, '<strong style="font-weight: 700; color: #1f2937;">$1</strong>');
  
  // [타임스탬프] 패턴
  text = text.replace(/\[(\d{2}:\d{2})\]/g, '<span style="display: inline-block; background: #e3f2fd; color: #1976d2; padding: 0.125rem 0.5rem; border-radius: 4px; font-size: 0.875rem; font-weight: 600; margin: 0 0.25rem;">$1</span>');
  
  // 줄바꿈을 <br>로
  text = text.replace(/\n/g, '<br>');
  
  return text;
}

/**
 * 스크립트 내용을 파싱하고 포맷팅
 */
function formatScriptContent(script: string) {
  if (!script) return null;

  const sections: React.ReactNode[] = [];
  
  // JSON 블록 찾기
  const jsonMatch = script.match(/```json\s*(\{[\s\S]*?\})\s*```/);
  
  if (jsonMatch) {
    try {
      const jsonData = JSON.parse(jsonMatch[1]);
      
      // JSON 이전 내용 (Hook, Introduction 등)
      const beforeJson = script.substring(0, script.indexOf('```json')).trim();
      
      if (beforeJson) {
        // Hook 섹션
        const hookMatch = beforeJson.match(/# Hook[\s\S]*?(?=# Introduction|# Main|$)/);
        if (hookMatch) {
          sections.push(
            <div key="hook" style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#ffffff',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1.5rem',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
            }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', opacity: 0.9 }}>
                🎬 HOOK (첫 10초)
              </div>
              <div 
                style={{ fontSize: '1rem', lineHeight: '1.8' }}
                dangerouslySetInnerHTML={{ __html: parseMarkdown(hookMatch[0].replace(/# Hook.*?\n/, '')) }}
              />
            </div>
          );
        }
        
        // Introduction 섹션
        const introMatch = beforeJson.match(/# Introduction[\s\S]*?(?=# Main|# Hook|$)/);
        if (introMatch) {
          sections.push(
            <div key="intro" style={{
              background: '#f0fdf4',
              border: '2px solid #86efac',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#15803d', marginBottom: '0.5rem' }}>
                👋 INTRODUCTION
              </div>
              <div 
                style={{ fontSize: '0.9375rem', lineHeight: '1.8', color: '#166534' }}
                dangerouslySetInnerHTML={{ __html: parseMarkdown(introMatch[0].replace(/# Introduction.*?\n/, '')) }}
              />
            </div>
          );
        }
      }
      
      // 메인 콘텐츠 (JSON 파싱)
      if (jsonData.main && Array.isArray(jsonData.main)) {
        jsonData.main.forEach((section: any, idx: number) => {
          sections.push(
            <div key={`main-${idx}`} style={{
              background: '#ffffff',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1.5rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              borderLeft: '4px solid #42a5f5'
            }}>
              {/* 섹션 제목 */}
              <h3 style={{
                margin: '0 0 1rem 0',
                fontSize: '1.125rem',
                fontWeight: 700,
                color: '#2c3e50',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span style={{ color: '#42a5f5' }}>#{idx + 1}</span>
                {section.section}
              </h3>
              
              {/* 타임스탬프 */}
              {section.timestamp && (
                <div style={{
                  display: 'inline-block',
                  background: '#e3f2fd',
                  color: '#1976d2',
                  padding: '0.375rem 0.875rem',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  marginBottom: '1rem'
                }}>
                  ⏱️ {section.timestamp}
                </div>
              )}
              
              {/* 내용 */}
              <div style={{
                color: '#374151',
                fontSize: '0.9375rem',
                lineHeight: '1.8',
                marginBottom: '1rem',
                whiteSpace: 'pre-wrap'
              }}>
                {section.content}
              </div>
              
              {/* 시각 제안 */}
              {(section.visualSuggestions || section.visual_suggestions) && (
                (() => {
                  const suggestions = section.visualSuggestions || section.visual_suggestions;
                  if (Array.isArray(suggestions) && suggestions.length > 0) {
                    return (
                      <div style={{ marginTop: '1rem' }}>
                        <div style={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: '#6b7280',
                          marginBottom: '0.5rem'
                        }}>
                          🎨 시각 제안
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: '0.5rem' 
                        }}>
                          {suggestions.map((visual: string, vidx: number) => (
                            <span key={vidx} style={{
                              background: '#f3f4f6',
                              padding: '0.5rem 0.875rem',
                              borderRadius: '6px',
                              fontSize: '0.8125rem',
                              color: '#4b5563',
                              border: '1px solid #e5e7eb'
                            }}>
                              {visual}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()
              )}
              
              {/* 핵심 포인트 */}
              {(section.keyPoints || section.key_points) && (
                (() => {
                  const points = section.keyPoints || section.key_points;
                  if (Array.isArray(points) && points.length > 0) {
                    return (
                      <div style={{ 
                        marginTop: '1rem',
                        background: '#fef3c7',
                        padding: '1rem',
                        borderRadius: '8px',
                        border: '1px solid #fcd34d'
                      }}>
                        <div style={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: '#92400e',
                          marginBottom: '0.5rem'
                        }}>
                          💡 핵심 포인트
                        </div>
                        <ul style={{ 
                          margin: 0,
                          paddingLeft: '1.5rem',
                          color: '#78350f',
                          fontSize: '0.875rem',
                          lineHeight: '1.6'
                        }}>
                          {points.map((point: string, pidx: number) => (
                            <li key={pidx} style={{ marginBottom: '0.25rem' }}>{point}</li>
                          ))}
                        </ul>
                      </div>
                    );
                  }
                  return null;
                })()
              )}
            </div>
          );
        });
      }
      
      // JSON 이후 내용 (Call to Action, Outro 등)
      const afterJson = script.substring(script.indexOf('```') + jsonMatch[0].length).trim();
      
      if (afterJson) {
        // Call to Action 섹션
        const ctaMatch = afterJson.match(/# Call to Action[\s\S]*?(?=# Outro|$)/);
        if (ctaMatch) {
          sections.push(
            <div key="cta" style={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: '#ffffff',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1.5rem',
              boxShadow: '0 4px 12px rgba(245, 87, 108, 0.3)'
            }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', opacity: 0.9 }}>
                🎯 CALL TO ACTION
              </div>
              <div 
                style={{ fontSize: '0.9375rem', lineHeight: '1.8' }}
                dangerouslySetInnerHTML={{ __html: parseMarkdown(ctaMatch[0].replace(/# Call to Action.*?\n/, '')) }}
              />
            </div>
          );
        }
        
        // Outro 섹션
        const outroMatch = afterJson.match(/# Outro[\s\S]*$/);
        if (outroMatch) {
          sections.push(
            <div key="outro" style={{
              background: '#fef2f2',
              border: '2px solid #fecaca',
              borderRadius: '12px',
              padding: '1.5rem'
            }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#991b1b', marginBottom: '0.5rem' }}>
                👋 OUTRO
              </div>
              <div 
                style={{ fontSize: '0.9375rem', lineHeight: '1.8', color: '#7f1d1d' }}
                dangerouslySetInnerHTML={{ __html: parseMarkdown(outroMatch[0].replace(/# Outro.*?\n/, '')) }}
              />
            </div>
          );
        }
      }
      
      return <div>{sections}</div>;
      
    } catch (e) {
      console.error('JSON 파싱 실패:', e);
    }
  }
  
  // JSON이 없으면 간단한 마크다운 파싱
  return (
    <div 
      style={{ 
        fontSize: '0.9375rem',
        lineHeight: '1.8',
        color: '#374151'
      }}
      dangerouslySetInnerHTML={{ __html: parseMarkdown(script) }}
    />
  );
}


